import React, { useState, useEffect } from 'react';
import { 
    createShippingRecord, 
    trackShipment, 
    updateShippingStatus,
    updateShippingTrackingNumber,
    completeDelivery,
    getShippingRecordBySaleId
} from '../../firebase/shipping';
import { getUserData } from '../../firebase/users';

const DialogShippingTracking = ({ isOpen, onClose, saleData, userRole }) => {
    const [trackingNumber, setTrackingNumber] = useState('');
    const [editTrackingNumber, setEditTrackingNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [trackingData, setTrackingData] = useState(null);
    const [shippingRecord, setShippingRecord] = useState(null);
    const [customerShippingInfo, setCustomerShippingInfo] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen && saleData) {
            console.log('DialogShippingTracking opened with saleData:', saleData);
            console.log('User role:', userRole);
            
            // Load existing shipping record
            loadExistingShippingRecord();
            loadSenderRecord();
        }
    }, [isOpen, saleData, userRole]);

    // Sender and Receiver is reverted, Sender = Buyer, Receiver = Seller
    const loadSenderRecord = async () => {
        const user = await getUserData(saleData.sender);
        setCustomerShippingInfo(user.shippingInfo);
        console.log(user.shippingInfo);
    }

    const loadExistingShippingRecord = async () => {
        try {
            const record = await getShippingRecordBySaleId(saleData.id);
            if (record) {
                console.log('Found existing shipping record:', record);
                setShippingRecord(record);
                
                // Auto-populate tracking number
                if (record.tracking_number && record.tracking_number !== 'PENDING') {
                    setTrackingNumber(record.tracking_number);
                    
                    // Automatically track the shipment
                    await autoTrackShipment(record.tracking_number, record);
                }
            } else {
                console.log('No existing shipping record found');
            }
        } catch (error) {
            console.error('Error loading shipping record:', error);
        }
    };

    // `record` is passed in rather than read from state: the callers below set
    // it immediately before calling, so the state value is still the previous
    // render's and would be null on the first open.
    const autoTrackShipment = async (trackingNum, record = shippingRecord) => {
        if (!trackingNum || trackingNum === 'PENDING') return;
        
        setLoading(true);
        setError('');

        try {
            console.log('Auto-tracking shipment with number:', trackingNum);
            const data = await trackShipment(trackingNum);
            setTrackingData(data);

            // Move the record out of 'label_created'. Without this the buyer's
            // "Confirm Delivery" control -- which only renders on 'in_transit'
            // -- never appeared, so the sale's funds stayed held indefinitely
            // unless someone happened to use the "Manual Tracking" box.
            //
            // Deliberately never set 'delivered' from carrier data: that status
            // means "the buyer confirmed and the seller was paid", and setting
            // it here would hide the confirm button and strand the payout.
            if (record?.id && record.status === 'label_created') {
                const events = data?.tracking_details || [];
                await updateShippingStatus(record.id, 'in_transit', events);
                setShippingRecord({ ...record, status: 'in_transit', tracking_updates: events });
            }
        } catch (error) {
            console.error('Error auto-tracking shipment:', error);
            setError('Failed to auto-track shipment. You can try tracking manually.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateShippingRecord = async () => {
        if (!trackingNumber.trim()) {
            setError('Please enter a tracking number');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const record = await createShippingRecord(
                saleData.id,
                trackingNumber.trim(),
                saleData.receiver, // seller ID
                saleData.sender    // buyer ID
            );
            
            setShippingRecord(record);
            setTrackingNumber('');
            autoTrackShipment(trackingNumber.trim(), record);
            alert('Shipping record created successfully!');
        } catch (error) {
            console.error('Error creating shipping record:', error);
            setError('Failed to create shipping record');
        } finally {
            setLoading(false);
        }
    };

    // A number the carrier cannot resolve leaves the record on
    // 'label_created', which is what blocks the buyer from confirming
    // delivery -- so the seller has to be able to correct it.
    const handleUpdateTrackingNumber = async () => {
        const next = editTrackingNumber.trim();

        if (!next) {
            setError('Enter a tracking number');
            return;
        }
        if (next === shippingRecord?.tracking_number) {
            setError('That is already the saved tracking number');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const updated = await updateShippingTrackingNumber(shippingRecord.id, next);
            setShippingRecord(updated);
            setTrackingNumber(next);
            setEditTrackingNumber('');
            setTrackingData(null);
            // Re-track straight away so a valid replacement advances the
            // record without the seller having to press anything else.
            await autoTrackShipment(next, updated);
        } catch (error) {
            console.error('Error updating tracking number:', error);
            setError(error.message || 'Failed to update the tracking number');
        } finally {
            setLoading(false);
        }
    };

    const handleTrackShipment = async () => {
        if (!trackingNumber.trim()) {
            setError('Please enter a tracking number');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const data = await trackShipment(trackingNumber.trim());
            setTrackingData(data);
            
            if (shippingRecord && shippingRecord.status === 'label_created') {
                // tracking_details is the shape this actually returns; the
                // old FedEx-style path always resolved undefined, so every
                // record was stored with an empty event list.
                const events = data?.tracking_details || [];
                await updateShippingStatus(shippingRecord.id, 'in_transit', events);
                setShippingRecord({ ...shippingRecord, status: 'in_transit', tracking_updates: events });
            }
        } catch (error) {
            console.error('Error tracking shipment:', error);
            setError('Failed to track shipment. Please check the tracking number.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmDelivery = async () => {
        if (!shippingRecord) {
            setError('No shipping record found');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await completeDelivery(shippingRecord.id, saleData.id);
            alert('Delivery confirmed and funds released to seller!');
            onClose();
        } catch (error) {
            console.error('Error confirming delivery:', error);
            setError('Failed to confirm delivery');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'text-yellow-500';
            case 'shipped': return 'text-blue-500';
            case 'in_transit': return 'text-blue-500';
            case 'delivered': return 'text-green-500';
            default: return 'text-gray-500';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'pending': return 'Pending';
            case 'shipped': return 'Shipped';
            case 'in_transit': return 'In Transit';
            case 'delivered': return 'Delivered';
            default: return 'Unknown';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{zIndex: 100}}>
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Shipping Tracking</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ✕
                    </button>
                </div>

                {/* Sale Information */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Sale Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="text-gray-600">Sale ID:</span>
                            <span className="ml-2 font-mono text-gray-800">{saleData?.id}</span>
                        </div>
                        <div>
                            {/* <span className="text-gray-600">Status:</span>
                            <span className={`ml-2 ${getStatusColor(saleData?.state)}`}>
                                {getStatusText(saleData?.state)}
                            </span> */}
                        </div>
                        <div>
                            <span className="text-gray-600">Price:</span>
                            <span className="ml-2 text-gray-800">${saleData?.offer_price || saleData?.price}</span>
                        </div>
                        <div>
                            <span className="text-gray-600">Quantity:</span>
                            <span className="ml-2 text-gray-800">{saleData?.count || 1}</span>
                        </div>
                    </div>
                </div>

                {/* Customer Shipping Information */}
                {customerShippingInfo && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="font-semibold text-gray-800 mb-2">Customer Shipping Information</h3>
                        <div className="mt-2">
                            <div className="ml-2 font-mono text-gray-800">
                                {customerShippingInfo?.name}
                            </div>
                            <div className="ml-2 font-mono text-gray-800">
                                {customerShippingInfo?.address?.street}
                            </div>
                            <div className="ml-2 font-mono text-gray-800">
                                {customerShippingInfo?.address?.city}, {customerShippingInfo?.address?.state} {customerShippingInfo?.address?.postalCode}
                            </div>
                            <div className="ml-2 font-mono text-gray-800">
                                {customerShippingInfo?.address?.country}
                            </div>
                        </div>
                    </div>
                )}

                {/* Current Shipping Status */}
                {shippingRecord && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                        <h3 className="font-semibold text-gray-800 mb-2">Current Shipping Status</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">Tracking Number:</span>
                                <span className="ml-2 font-mono text-gray-800">{shippingRecord.tracking_number}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Status:</span>
                                <span className={`ml-2 ${getStatusColor(shippingRecord.status)}`}>
                                    {trackingData ? trackingData.status : 'Unknown'}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-600">Carrier:</span>
                                <span className="ml-2 text-gray-800">{trackingData ? trackingData.carrier : 'Unknown'}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Created:</span>
                                <span className="ml-2 text-gray-800">
                                    {new Date(shippingRecord.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                        
                        {/* Enhanced FedEx Information */}
                        {shippingRecord.fedex_data && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className="font-medium text-gray-800 mb-2">FedEx Shipping Details</h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">Service:</span>
                                        <span className="ml-2 text-gray-800">{shippingRecord.fedex_data.serviceName || 'FedEx Express Saver'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Ship Date:</span>
                                        <span className="ml-2 text-gray-800">{shippingRecord.fedex_data.shipDate || 'N/A'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Estimated Delivery:</span>
                                        <span className="ml-2 text-gray-800">{shippingRecord.fedex_data.estimatedDelivery || 'N/A'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Shipping Cost:</span>
                                        <span className="ml-2 text-gray-800">${shippingRecord.fedex_data.shippingCost || '0.00'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Package Weight:</span>
                                        <span className="ml-2 text-gray-800">{shippingRecord.fedex_data.packageWeight || 'N/A'} lbs</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Package Dimensions:</span>
                                        <span className="ml-2 text-gray-800">
                                            {shippingRecord.fedex_data.packageDimensions ? 
                                                `${shippingRecord.fedex_data.packageDimensions.length}" × ${shippingRecord.fedex_data.packageDimensions.width}" × ${shippingRecord.fedex_data.packageDimensions.height}"` : 
                                                'N/A'
                                            }
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Shipping Route */}
                                {shippingRecord.fedex_data.fromAddress && shippingRecord.fedex_data.toAddress && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                        <div className="text-sm">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-gray-600">Route:</span>
                                                <span className="text-gray-800">📍 {shippingRecord.fedex_data.fromAddress.address?.city}, {shippingRecord.fedex_data.fromAddress.address?.state}</span>
                                                <span className="text-gray-800">→</span>
                                                <span className="text-gray-800">📍 {shippingRecord.fedex_data.toAddress.address?.city}, {shippingRecord.fedex_data.toAddress.address?.state}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Public Tracking Link */}
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="text-sm">
                                        <p className="text-gray-600 mb-2">For detailed tracking information:</p>
                                        <a 
                                            href={`https://www.fedex.com/fedextrack/?trknbr=${shippingRecord.tracking_number}`}
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                                        >
                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                            Track on FedEx Website
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Add Tracking Number (for sellers) */}
                {userRole === 'seller' && !shippingRecord && (
                    <div className="mb-6 p-4 bg-green-50 rounded-lg">
                        <h3 className="font-semibold text-gray-800 mb-2">Add Shipping Information</h3>
                        <p className="text-sm text-gray-600 mb-2">
                            The carrier is detected from the tracking number. Once it registers with the
                            carrier, the buyer can confirm delivery and your payment is released.
                        </p>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                placeholder="Enter tracking number (UPS, USPS, FedEx)"
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                            />
                            <button
                                onClick={handleCreateShippingRecord}
                                disabled={loading}
                                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
                            >
                                {loading ? 'Creating...' : 'Create Shipping Record'}
                            </button>
                        </div>
                        
                        {/* Test Buttons
                        <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                            <h4 className="font-medium text-gray-800 mb-2">Test Tracking Numbers:</h4>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setTrackingNumber(FEDEX_TEST_TRACKING_NUMBERS.VALID)}
                                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                >
                                    Valid Test
                                </button>
                                <button
                                    onClick={() => setTrackingNumber(FEDEX_TEST_TRACKING_NUMBERS.INVALID)}
                                    className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                                >
                                    Invalid Test
                                </button>
                                <button
                                    onClick={() => setTrackingNumber(FEDEX_TEST_TRACKING_NUMBERS.SAMPLE)}
                                    className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                                >
                                    Sample Test
                                </button>
                            </div>
                        </div> */}
                    </div>
                )}

                {/* Track Shipment */}
                {shippingRecord && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                        <h3 className="font-semibold text-gray-800 mb-2">Track Shipment</h3>
                        
                        {/* Saved Tracking Information */}
                        {shippingRecord.tracking_number && shippingRecord.tracking_number !== 'PENDING' && (
                            <div className="mb-4 p-3 bg-white rounded border">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-gray-800">Saved Tracking Number:</h4>
                                    <span className="font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                                        {shippingRecord.tracking_number}
                                    </span>
                                </div>
                                <button
                                    onClick={() => autoTrackShipment(shippingRecord.tracking_number)}
                                    disabled={loading}
                                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
                                >
                                    {loading ? 'Tracking...' : '🔄 Refresh Tracking'}
                                </button>

                                {userRole === 'seller' && shippingRecord.status !== 'delivered' && (
                                    <div className="mt-4 pt-4 border-t">
                                        <h4 className="font-medium text-gray-800 mb-1">Correct the tracking number</h4>
                                        <p className="text-sm text-gray-600 mb-2">
                                            {shippingRecord.status === 'label_created'
                                                ? "The carrier hasn't recognised this number yet. If it was mistyped, replace it here — the buyer can only confirm delivery once it resolves."
                                                : 'Replace the number if you shipped under a different one.'}
                                        </p>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                placeholder="Enter the correct tracking number"
                                                value={editTrackingNumber}
                                                onChange={(e) => setEditTrackingNumber(e.target.value)}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                                            />
                                            <button
                                                onClick={handleUpdateTrackingNumber}
                                                disabled={loading || !editTrackingNumber.trim()}
                                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                                            >
                                                {loading ? 'Saving...' : 'Save & Re-track'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Manual Tracking (Optional) */}
                        <div className="border-t pt-4">
                            <h4 className="font-medium text-gray-800 mb-2">Manual Tracking (Optional)</h4>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Enter different tracking number"
                                    value={trackingNumber}
                                    onChange={(e) => setTrackingNumber(e.target.value)}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                                />
                                <button
                                    onClick={handleTrackShipment}
                                    disabled={loading || !trackingNumber.trim()}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                                >
                                    {loading ? 'Tracking...' : 'Track Manually'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Confirm Delivery (for buyers) */}
                {userRole === 'buyer' && shippingRecord && shippingRecord.status === 'in_transit' && (
                    <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
                        <h3 className="font-semibold text-gray-800 mb-2">Confirm Delivery</h3>
                        <p className="text-sm text-gray-600 mb-2">
                            If you have received the item, confirm delivery to release funds to the seller.
                        </p>
                        <button
                            onClick={handleConfirmDelivery}
                            disabled={loading}
                            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50"
                        >
                            {loading ? 'Confirming...' : 'Confirm Delivery & Release Funds'}
                        </button>
                    </div>
                )}

                {/* Tracking Results */}
                {trackingData && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="font-semibold text-gray-800 mb-2">Tracking Results</h3>
                        <div className="text-sm">
                            {trackingData.tracking_details?.map((event, index) => (
                                <div key={index} className="border-b border-gray-200 py-2">
                                    <div className="font-medium">{event.message}</div>
                                    <div className="text-gray-600">
                                        {(new Date(event.datetime)).toLocaleString()} - {event.tracking_location.city}, {event.tracking_location.state}, {event.tracking_location.country} - {event.status}
                                    </div>
                                </div>
                            )) || (
                                <div className="text-gray-600">No tracking events found</div>
                            )}
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-600 text-sm">{error}</p>
                    </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DialogShippingTracking; 